/**
 * /admin/orders — admin-only order tracker console.
 * Create orders, advance the cure stage, copy/resend the client tracking link.
 * Gated by the academy_token (admin/admin_role required server-side).
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Copy, Mail, Trash2, ChevronRight, X, ShieldAlert } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });
const PUBLIC_ORIGIN = 'https://prsvr.store';

const emptyItem = () => ({ title: '', variant: '', quantity: 1 });

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [stageSets, setStageSets] = useState({});
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creds, setCreds] = useState({ email: '', password: '' });
  const [loggingIn, setLoggingIn] = useState(false);

  const [form, setForm] = useState({
    customer_name: '', customer_email: '', category: 'dila', note: '', notify: true,
    items: [emptyItem()],
  });

  const load = async () => {
    try {
      const [ordersRes, setsRes] = await Promise.all([
        api.get('/orders', { headers: authHeaders() }),
        api.get('/orders/stage-sets', { headers: authHeaders() }),
      ]);
      setOrders(ordersRes.data.orders || []);
      setStageSets(setsRes.data.stage_sets || {});
      setLabels(setsRes.data.labels || {});
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) setUnauthorized(true);
      else toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const doLogin = async () => {
    if (!creds.email || !creds.password) { toast.error('Enter email and password'); return; }
    setLoggingIn(true);
    try {
      const res = await api.post('/academy/auth/login', creds);
      const token = res.data.token;
      if (!token) throw new Error('no token');
      localStorage.setItem('academy_token', token);
      setUnauthorized(false);
      setLoading(true);
      await load();
      toast.success('Signed in');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Login failed');
    } finally {
      setLoggingIn(false);
    }
  };

  const createOrder = async () => {
    if (!form.customer_name.trim() || !/^\S+@\S+\.\S+$/.test(form.customer_email)) {
      toast.error('Enter a valid name and email');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        items: form.items.filter((i) => i.title.trim()).map((i) => ({ ...i, quantity: Number(i.quantity) || 1 })),
      };
      const res = await api.post('/orders', payload, { headers: authHeaders() });
      toast.success(`Order ${res.data.order.order_number} created${res.data.emailed ? ' · email sent' : ''}`);
      setShowCreate(false);
      setForm({ customer_name: '', customer_email: '', category: 'dila', note: '', notify: true, items: [emptyItem()] });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create order');
    } finally {
      setSaving(false);
    }
  };

  const advanceStage = async (order, stage) => {
    try {
      const res = await api.patch(`/orders/${order.id}/stage`, { stage, notify: true }, { headers: authHeaders() });
      toast.success(`${order.order_number} → ${stage}${res.data.emailed ? ' · client emailed' : ''}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update stage');
    }
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${PUBLIC_ORIGIN}/track/${token}`);
    toast.success('Tracking link copied');
  };

  const resend = async (order) => {
    try {
      const res = await api.post(`/orders/${order.id}/resend`, {}, { headers: authHeaders() });
      toast.success(res.data.emailed ? 'Tracking link emailed' : 'Email not configured');
    } catch { toast.error('Failed to resend'); }
  };

  const removeOrder = async (order) => {
    if (!window.confirm(`Delete order ${order.order_number}?`)) return;
    try {
      await api.delete(`/orders/${order.id}`, { headers: authHeaders() });
      toast.success('Order deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) {
    return <div className="min-h-screen bg-stone-950 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div>;
  }

  if (unauthorized) {
    return (
      <div className="min-h-screen bg-stone-950 text-stone-100 flex items-center justify-center px-6" data-testid="admin-orders-unauthorized">
        <div className="w-full max-w-sm">
          <ShieldAlert className="w-10 h-10 text-amber-400 mx-auto mb-4" />
          <div className="text-[10px] tracking-[0.4em] uppercase text-amber-400 font-black text-center">PRSVR · Fulfillment</div>
          <h1 className="text-2xl font-black uppercase mb-2 text-center">Order Tracker Admin</h1>
          <p className="text-sm text-stone-400 mb-6 text-center">Sign in with your admin account.</p>
          <div className="space-y-3">
            <input
              type="email"
              className="w-full bg-stone-900 border border-stone-700 rounded px-3 py-3 text-sm"
              placeholder="Admin email"
              value={creds.email}
              onChange={(e) => setCreds({ ...creds, email: e.target.value })}
              data-testid="admin-login-email"
            />
            <input
              type="password"
              className="w-full bg-stone-900 border border-stone-700 rounded px-3 py-3 text-sm"
              placeholder="Password"
              value={creds.password}
              onChange={(e) => setCreds({ ...creds, password: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && doLogin()}
              data-testid="admin-login-password"
            />
            <button
              onClick={doLogin}
              disabled={loggingIn}
              className="w-full bg-amber-400 text-stone-950 px-6 py-3 text-xs font-black tracking-[0.2em] uppercase hover:bg-amber-300 disabled:opacity-50"
              data-testid="admin-login-submit"
            >
              {loggingIn ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Sign In'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100" data-testid="admin-orders-page">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-amber-400 font-black">PRSVR · Fulfillment</div>
            <h1 className="text-3xl font-black uppercase tracking-tight">Order Tracker</h1>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 bg-amber-400 text-stone-950 px-5 py-3 text-xs font-black tracking-[0.2em] uppercase hover:bg-amber-300 transition-colors"
            data-testid="admin-create-order-btn"
          >
            <Plus className="w-4 h-4" /> New Order
          </button>
        </div>

        {orders.length === 0 ? (
          <div className="py-24 text-center text-stone-500 border border-stone-800 rounded" data-testid="admin-orders-empty">
            No orders yet. Create one to generate a client tracking link.
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-orders-list">
            {orders.map((o) => {
              const stages = o.stages || [];
              return (
                <div key={o.id} className="border border-stone-800 bg-stone-900/60 rounded-lg p-5" data-testid={`admin-order-${o.order_number}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div>
                      <div className="font-black tracking-wide">{o.order_number} <span className="text-[10px] uppercase tracking-widest text-amber-400 ml-2">{labels[o.category] || o.category}</span></div>
                      <div className="text-sm text-stone-400">{o.customer_name} · {o.customer_email}</div>
                      <div className="text-xs text-stone-500 mt-1">
                        {(o.items || []).map((it) => `${it.title}${it.variant ? ' (' + it.variant + ')' : ''} ×${it.quantity}`).join(', ') || 'No items listed'}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => copyLink(o.token)} title="Copy tracking link" className="p-2 hover:bg-stone-800 rounded" data-testid={`admin-copy-${o.order_number}`}><Copy className="w-4 h-4" /></button>
                      <button onClick={() => resend(o)} title="Resend email" className="p-2 hover:bg-stone-800 rounded" data-testid={`admin-resend-${o.order_number}`}><Mail className="w-4 h-4" /></button>
                      <button onClick={() => removeOrder(o)} title="Delete" className="p-2 hover:bg-stone-800 rounded text-red-400" data-testid={`admin-delete-${o.order_number}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>

                  {/* Stage pills */}
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {stages.map((s, i) => {
                      const active = i === o.current_index;
                      const done = i < o.current_index;
                      return (
                        <button
                          key={s}
                          onClick={() => advanceStage(o, s)}
                          className={`text-[10px] font-black tracking-wider uppercase px-2.5 py-1.5 rounded transition-colors ${
                            active ? 'bg-amber-400 text-stone-950' : done ? 'bg-stone-700 text-stone-200' : 'bg-stone-800 text-stone-500 hover:bg-stone-700'
                          }`}
                          data-testid={`admin-stage-${o.order_number}-${i}`}
                        >
                          {s}
                        </button>
                      );
                    })}
                  </div>
                  <div className="text-[11px] text-stone-500 flex items-center gap-1">
                    Current: <span className="text-amber-400 font-bold">{o.current_stage}</span>
                    <ChevronRight className="w-3 h-3" /> click a stage to advance & notify client
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="admin-create-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowCreate(false)} />
          <div className="relative bg-stone-900 border border-stone-700 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-black uppercase tracking-wide">New Order</h2>
              <button onClick={() => setShowCreate(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <input className="w-full bg-stone-800 border border-stone-700 rounded px-3 py-2.5 text-sm" placeholder="Customer name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="admin-form-name" />
              <input className="w-full bg-stone-800 border border-stone-700 rounded px-3 py-2.5 text-sm" placeholder="Customer email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} data-testid="admin-form-email" />
              <select className="w-full bg-stone-800 border border-stone-700 rounded px-3 py-2.5 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="admin-form-category">
                {Object.keys(stageSets).map((c) => <option key={c} value={c}>{labels[c] || c}</option>)}
              </select>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 pt-1">Items</div>
              {form.items.map((it, idx) => (
                <div key={idx} className="flex gap-2">
                  <input className="flex-1 bg-stone-800 border border-stone-700 rounded px-3 py-2 text-sm" placeholder="Piece (e.g. Lux Tee)" value={it.title} onChange={(e) => { const items = [...form.items]; items[idx].title = e.target.value; setForm({ ...form, items }); }} data-testid={`admin-form-item-title-${idx}`} />
                  <input className="w-28 bg-stone-800 border border-stone-700 rounded px-3 py-2 text-sm" placeholder="001 / L" value={it.variant} onChange={(e) => { const items = [...form.items]; items[idx].variant = e.target.value; setForm({ ...form, items }); }} />
                  <input type="number" min="1" className="w-16 bg-stone-800 border border-stone-700 rounded px-2 py-2 text-sm" value={it.quantity} onChange={(e) => { const items = [...form.items]; items[idx].quantity = e.target.value; setForm({ ...form, items }); }} />
                </div>
              ))}
              <button onClick={() => setForm({ ...form, items: [...form.items, emptyItem()] })} className="text-xs text-amber-400 font-bold">+ Add item</button>
              <label className="flex items-center gap-2 text-sm text-stone-300 pt-1">
                <input type="checkbox" checked={form.notify} onChange={(e) => setForm({ ...form, notify: e.target.checked })} data-testid="admin-form-notify" />
                Email the client their tracking link now
              </label>
              <button onClick={createOrder} disabled={saving} className="w-full bg-amber-400 text-stone-950 px-4 py-3 text-xs font-black tracking-[0.2em] uppercase hover:bg-amber-300 disabled:opacity-50 mt-2" data-testid="admin-form-submit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Create & Generate Link'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
